/**
 * DocumentTypeIcon — small file-type badge for RDL rows + preview headers.
 * Picks a lucide icon + tinted box based on MIME. No external libraries.
 */

import { FileText, Image as ImageIcon, FileArchive, File } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface DocumentTypeIconProps {
  contentType: string;
  size?: number;
}

interface Spec {
  Icon: LucideIcon;
  bg: string;
  color: string;
  label: string;
}

function specFor(contentType: string): Spec {
  const ct = contentType.toLowerCase();
  if (ct === 'application/pdf' || ct.endsWith('/pdf')) {
    return {
      Icon: FileText,
      bg: 'var(--pcb-red-light, rgba(177, 5, 5, 0.08))',
      color: 'var(--pcb-red)',
      label: 'PDF',
    };
  }
  if (ct.startsWith('image/')) {
    return {
      Icon: ImageIcon,
      bg: 'rgba(41, 104, 173, 0.08)',
      color: 'var(--pcb-blue)',
      label: 'Image',
    };
  }
  if (ct.includes('zip') || ct.includes('compressed') || ct.includes('rar')) {
    return {
      Icon: FileArchive,
      bg: 'rgba(56, 103, 50, 0.08)',
      color: 'var(--pcb-green)',
      label: 'Archive',
    };
  }
  return {
    Icon: File,
    bg: 'var(--ink-100)',
    color: 'var(--ink-600)',
    label: 'File',
  };
}

export function DocumentTypeIcon({ contentType, size = 16 }: DocumentTypeIconProps): JSX.Element {
  const spec = specFor(contentType);
  const Icon = spec.Icon;
  return (
    <span
      aria-label={spec.label}
      title={spec.label}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 32,
        height: 32,
        minWidth: 32,
        borderRadius: 'var(--r-sm)',
        background: spec.bg,
        color: spec.color,
        flexShrink: 0,
      }}
    >
      <Icon size={size} aria-hidden />
    </span>
  );
}
